import { z } from "zod";
import { isoDate, isoDateTime, optionalText, text, uuid } from "./common";
import type { TaxScheme } from "./settings";

/*
 * Financial core (Phase 4): invoices, payments (receipts), allocations, refunds, credit notes and the
 * patient ledger. Money is always integer minor units (paise) in the clinic's currency.
 *
 * Rules (enforced in the API AND the database, migration 0011):
 *  - Invoices start as drafts (editable, unnumbered). Issuing numbers and freezes them. After issue
 *    the only corrections are credit notes (reduce), void (nothing paid or credited) and payments.
 *  - Payments are receipts. The unallocated part of a payment is the patient's advance; it can be
 *    applied to invoices later or refunded.
 *  - The patient ledger is append-only. Balance = invoiced − credited − paid + refunded, so
 *    "balance = open invoice balances − advance" always holds.
 */

const minor = z.number().int().min(0).max(1_000_000_000_00);
const positiveMinor = z.number().int().min(1).max(1_000_000_000_00);
const quantity = z
  .number()
  .positive()
  .max(9999)
  .refine((v) => Math.abs(Math.round(v * 100) - v * 100) < 1e-6, "At most two decimals");
const toothCode = z.string().regex(/^([1-4][1-8]|[5-8][1-5])$/, "FDI tooth number (11–48, 51–85)");
const regionCode = z.string().trim().toUpperCase().regex(/^[A-Z0-9]{1,10}$/, "Region / state code, e.g. 32");

/* -------------------------------------------------------------------------- payment methods */

export const PAYMENT_METHOD_KINDS = ["cash", "card", "upi", "bank_transfer", "cheque", "gateway", "insurance", "other"] as const;
export type PaymentMethodKind = (typeof PAYMENT_METHOD_KINDS)[number];

export const paymentMethodInput = z.object({
  name: text(80),
  kind: z.enum(PAYMENT_METHOD_KINDS),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(1000).default(0),
});
export type PaymentMethodInput = z.infer<typeof paymentMethodInput>;

export interface PaymentMethod {
  id: string;
  code: string;
  name: string;
  kind: PaymentMethodKind;
  isActive: boolean;
  sortOrder: number;
}

/* ---------------------------------------------------------------------------------- invoices */

export const INVOICE_STATUSES = ["draft", "issued", "partially_paid", "paid", "void"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/**
 * Where a line comes from:
 *  - plan_item: an accepted treatment-plan item, billed at the plan's quoted price and discount;
 *  - session:   an unplanned procedure recorded in a visit, at today's service price;
 *  - service:   any catalogue service at today's price;
 *  - custom:    free text with a price (e.g. lab charge); tax only if a tax category is chosen.
 * Tax always comes from the service's (or chosen) tax category in force on the invoice date.
 */
export const invoiceLineInput = z.discriminatedUnion("source", [
  z.object({
    source: z.literal("plan_item"),
    planItemId: uuid,
    discountMinor: minor.optional(),
    description: optionalText(300),
  }),
  z.object({
    source: z.literal("session"),
    sessionId: uuid,
    discountMinor: minor.default(0),
    description: optionalText(300),
  }),
  /** A completed therapy session (Ayurveda), priced from its therapy's billing service. */
  z.object({
    source: z.literal("therapy_session"),
    therapySessionId: uuid,
    discountMinor: minor.default(0),
    description: optionalText(300),
  }),
  z.object({
    source: z.literal("service"),
    serviceId: uuid,
    quantity: quantity.default(1),
    discountMinor: minor.default(0),
    toothCode: toothCode.nullish().transform((v) => v ?? null),
    doctorId: uuid.nullish().transform((v) => v ?? null),
    description: optionalText(300),
  }),
  z.object({
    source: z.literal("custom"),
    description: text(300),
    quantity: quantity.default(1),
    unitPriceMinor: minor,
    discountMinor: minor.default(0),
    priceIncludesTax: z.boolean().default(false),
    taxCategoryId: uuid.nullish().transform((v) => v ?? null),
    /** HSN / SAC printed on the line (e.g. 3004 for medicines). */
    taxClassificationCode: z.string().trim().toUpperCase().regex(/^[A-Z0-9.]{2,12}$/).nullish().transform((v) => v ?? null),
    doctorId: uuid.nullish().transform((v) => v ?? null),
  }),
]);
export type InvoiceLineInput = z.infer<typeof invoiceLineInput>;

export const createInvoiceInput = z.object({
  branchId: uuid,
  doctorId: uuid.nullish().transform((v) => v ?? null),
  visitId: uuid.nullish().transform((v) => v ?? null),
  dueDate: isoDate.nullish().transform((v) => v ?? null),
  notes: optionalText(2000),
  /** Only for supply outside the branch's region (India: inter-state, billed as IGST). Defaults to the branch. */
  placeOfSupplyRegionCode: regionCode.nullish().transform((v) => v ?? null),
  buyerTaxRegistrationId: optionalText(30),
  lines: z.array(invoiceLineInput).min(1, "Add at least one line").max(100),
});
export type CreateInvoiceInput = z.infer<typeof createInvoiceInput>;

export const updateInvoiceInput = createInvoiceInput.extend({ version: z.number().int().min(1) });
export const issueInvoiceInput = z.object({ version: z.number().int().min(1) });
export const voidInvoiceInput = z.object({ version: z.number().int().min(1), reason: text(500) });

export const invoiceListQuery = z.object({
  status: z.enum([...INVOICE_STATUSES, "open"]).optional(),
  patientId: uuid.optional(),
  branchId: uuid.optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export interface InvoiceLineTax {
  component: string;
  ratePercent: number;
  taxableMinor: number;
  amountMinor: number;
}

export interface InvoiceLine {
  id: string;
  lineNo: number;
  source: InvoiceLineInput["source"];
  serviceId: string | null;
  planItemId: string | null;
  sessionId: string | null;
  therapySessionId: string | null;
  doctorId: string | null;
  description: string;
  toothCode: string | null;
  taxClassificationCode: string | null;
  taxCategoryId: string | null;
  quantity: number;
  unitPriceMinor: number;
  priceIncludesTax: boolean;
  discountMinor: number;
  taxRatePercent: number | null;
  taxableMinor: number;
  taxMinor: number;
  totalMinor: number;
  /** Already credited on this line by issued credit notes. */
  creditedMinor: number;
  taxes: InvoiceLineTax[];
}

export interface InvoiceListItem {
  id: string;
  number: string | null;
  status: InvoiceStatus;
  patient: { id: string; fullName: string; patientCode: string };
  branch: { id: string; name: string };
  issueDate: string | null;
  dueDate: string | null;
  currency: string;
  totalMinor: number;
  amountPaidMinor: number;
  amountCreditedMinor: number;
  balanceMinor: number;
  createdAt: string;
}

export interface Invoice extends InvoiceListItem {
  doctor: { id: string; fullName: string } | null;
  visitId: string | null;
  subtotalMinor: number;
  discountMinor: number;
  taxMinor: number;
  taxScheme: TaxScheme;
  sellerTaxRegistrationId: string | null;
  sellerRegionCode: string | null;
  placeOfSupplyRegionCode: string | null;
  buyerTaxRegistrationId: string | null;
  notes: string | null;
  issuedAt: string | null;
  issuedBy: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  version: number;
  lines: InvoiceLine[];
  /** Tax summary by component and rate (what GST returns need). */
  taxSummary: { component: string; ratePercent: number; taxableMinor: number; amountMinor: number }[];
  payments: { allocationId: string; paymentId: string; receiptNumber: string; method: string; amountMinor: number; allocatedAt: string; reversedAt: string | null }[];
  creditNotes: { id: string; number: string | null; status: string; totalMinor: number; issueDate: string | null; reasonCode: CreditNoteReason }[];
}

/* -------------------------------------------------------------------------- unbilled treatment */

export interface UnbilledItem {
  source: "plan_item" | "session" | "therapy_session";
  id: string;
  description: string;
  toothCode: string | null;
  doctor: { id: string; fullName: string } | null;
  date: string;
  /** plan_item: the quoted line total; session: an estimate at today's price (null if unpriced). */
  estimatedTotalMinor: number | null;
  status: string;
  planTitle: string | null;
  visitId: string | null;
}

/* ------------------------------------------------------------------------- payments (receipts) */

export const PAYMENT_STATUSES = ["pending", "completed", "failed", "reversed"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const allocationInput = z.object({ invoiceId: uuid, amountMinor: positiveMinor });

export const recordPaymentInput = z.object({
  patientId: uuid,
  branchId: uuid,
  paymentMethodId: uuid,
  amountMinor: positiveMinor,
  /** When the money was received (defaults to now; cannot be in the future). */
  receivedAt: isoDateTime.nullish().transform((v) => v ?? null),
  reference: optionalText(100),
  notes: optionalText(1000),
  /** Explicit allocations. When omitted the payment is applied to open invoices, oldest first. */
  allocations: z.array(allocationInput).max(50).optional(),
  /** false = keep the whole amount as advance. */
  autoAllocate: z.boolean().default(true),
});
export type RecordPaymentInput = z.infer<typeof recordPaymentInput>;

export const allocatePaymentInput = z.object({ allocations: z.array(allocationInput).min(1).max(50) });
export const unallocateInput = z.object({ allocationId: uuid, reason: text(500) });
export const reversePaymentInput = z.object({ version: z.number().int().min(1), reason: text(500) });
export const refundInput = z.object({
  amountMinor: positiveMinor,
  refundMethodId: uuid,
  reason: text(500),
  reference: optionalText(100),
});

export const paymentListQuery = z.object({
  patientId: uuid.optional(),
  branchId: uuid.optional(),
  methodId: uuid.optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

export interface PaymentRefund {
  id: string;
  number: string;
  amountMinor: number;
  method: string;
  reason: string;
  reference: string | null;
  status: string;
  processedAt: string | null;
}

export interface Payment {
  id: string;
  number: string;
  status: PaymentStatus;
  patient: { id: string; fullName: string; patientCode: string };
  branch: { id: string; name: string };
  method: { id: string; name: string; kind: PaymentMethodKind };
  receivedAt: string;
  currency: string;
  amountMinor: number;
  allocatedMinor: number;
  refundedMinor: number;
  /** Advance left on this receipt: amount − active allocations − live refunds. */
  unallocatedMinor: number;
  reference: string | null;
  notes: string | null;
  receivedBy: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  version: number;
  allocations: { id: string; invoiceId: string; invoiceNumber: string | null; amountMinor: number; allocatedAt: string; reversedAt: string | null; reversalReason: string | null }[];
  refunds: PaymentRefund[];
}

/* ------------------------------------------------------------------------------ credit notes */

export const CREDIT_NOTE_REASONS = ["service_not_rendered", "pricing_error", "discount", "goodwill", "duplicate", "other"] as const;
export type CreditNoteReason = (typeof CREDIT_NOTE_REASONS)[number];

export const createCreditNoteInput = z.object({
  invoiceVersion: z.number().int().min(1),
  reasonCode: z.enum(CREDIT_NOTE_REASONS),
  reason: optionalText(500),
  /** Amount to credit per invoice line, tax-inclusive. */
  lines: z.array(z.object({ invoiceItemId: uuid, amountMinor: positiveMinor })).min(1).max(100),
});
export type CreateCreditNoteInput = z.infer<typeof createCreditNoteInput>;

export interface CreditNote {
  id: string;
  number: string | null;
  status: "draft" | "issued" | "void";
  invoiceId: string;
  invoiceNumber: string | null;
  patient: { id: string; fullName: string; patientCode: string };
  branchId: string;
  currency: string;
  reasonCode: CreditNoteReason;
  reason: string | null;
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  issueDate: string | null;
  issuedAt: string | null;
  lines: { id: string; lineNo: number; invoiceItemId: string; description: string; taxableMinor: number; taxMinor: number; totalMinor: number; taxes: InvoiceLineTax[] }[];
  /** Payments moved back to advance to make room for this credit (paid invoices). */
  releasedToAdvanceMinor: number;
}

/* ---------------------------------------------------------------------------- patient ledger */

export const LEDGER_ENTRY_TYPES = ["invoice", "payment", "refund", "credit_note", "adjustment", "write_off"] as const;

export interface LedgerEntry {
  id: string;
  occurredAt: string;
  entryType: (typeof LEDGER_ENTRY_TYPES)[number];
  posting: "original" | "reversal" | "write_off";
  sourceType: string;
  sourceId: string;
  documentNumber: string | null;
  description: string | null;
  debitMinor: number;
  creditMinor: number;
  runningBalanceMinor: number;
}

export interface PatientBillingSummary {
  currency: string;
  /** Ledger balance: positive = the patient owes the clinic, negative = advance held. */
  balanceMinor: number;
  /** Sum of open invoice balances. */
  dueMinor: number;
  /** Unapplied money on receipts (can be applied to invoices or refunded). */
  advanceMinor: number;
  openInvoices: number;
  unbilledItems: number;
}

/* ---------------------------------------------------------------------------------- reports */

export const billingReportQuery = z
  .object({ from: isoDate, to: isoDate, branchId: uuid.optional() })
  .refine((q) => q.from <= q.to, { message: "From must be on or before To", path: ["to"] });

export interface BillingSummaryReport {
  from: string;
  to: string;
  branchId: string | null;
  currency: string;
  invoiced: { count: number; subtotalMinor: number; discountMinor: number; taxMinor: number; totalMinor: number };
  credited: { count: number; taxMinor: number; totalMinor: number };
  /** Invoiced − credited: net sales for the period. */
  netSalesMinor: number;
  collections: { totalMinor: number; count: number; byMethod: { methodId: string; name: string; kind: PaymentMethodKind; count: number; amountMinor: number }[] };
  refunds: { totalMinor: number; count: number; byMethod: { methodId: string; name: string; count: number; amountMinor: number }[] };
  /** Collections − refunds (cash actually kept). */
  netCollectionsMinor: number;
  /** Output tax by component and rate: invoices minus credit notes. */
  taxBreakup: { component: string; ratePercent: number; taxableMinor: number; taxMinor: number }[];
  byDoctor: { doctorId: string | null; name: string; netSalesMinor: number }[];
  byService: { serviceId: string | null; name: string; quantity: number; netSalesMinor: number }[];
}

export interface OutstandingRow {
  patient: { id: string; fullName: string; patientCode: string; phone: string | null };
  openInvoices: number;
  oldestIssueDate: string;
  balanceMinor: number;
  advanceMinor: number;
  buckets: { current: number; d31_60: number; d61_90: number; over90: number };
}

export interface OutstandingReport {
  asOf: string;
  currency: string;
  rows: OutstandingRow[];
  totals: { balanceMinor: number; current: number; d31_60: number; d61_90: number; over90: number };
}

/* ---------------------------------------------------------------------------------- printing */

export interface BillingClinicHeader {
  name: string;
  legalName: string | null;
  addressLines: string[];
  phone: string | null;
  email: string | null;
  taxRegistrationLabel: string | null;
  taxRegistrationNumber: string | null;
  footerNote: string;
}

export interface InvoicePrint {
  clinic: BillingClinicHeader;
  branch: { name: string; addressLines: string[]; regionCode: string | null };
  invoice: Invoice;
  patient: { name: string; code: string; phone: string | null; addressLines: string[] };
  amountInWords: string | null;
  watermark: string | null;
}

export interface ReceiptPrint {
  clinic: BillingClinicHeader;
  payment: Payment;
  /** Balance still owed by the patient after this receipt (ledger, as of printing). */
  patientBalanceMinor: number;
  amountInWords: string | null;
  watermark: string | null;
}

/** POST /api/patients/:id/invoices/preview: what saving would store, without saving. */
export interface InvoicePreview {
  currency: string;
  subtotalMinor: number;
  discountMinor: number;
  taxMinor: number;
  totalMinor: number;
  lines: {
    lineNo: number;
    description: string;
    toothCode: string | null;
    quantity: number;
    unitPriceMinor: number;
    priceIncludesTax: boolean;
    discountMinor: number;
    taxRatePercent: number | null;
    taxableMinor: number;
    taxMinor: number;
    totalMinor: number;
    taxes: { component: string; ratePercent: number; amountMinor: number }[];
  }[];
}
